// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Services
import { CoreService } from '../../../../shared/services';
import { DeviceService } from '../services/device.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';
import { DialogService } from '../../../../shared/services/dialog.service';

// Models
import { DeviceType, DeviceViewModel } from '../models/device.model';
import { AppCleanup } from '../../../../shared/models';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Store
import { DeviceActions } from './device.action';

// Enums
import { StatusType } from '../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';
import { StorageActions } from '../../../../shared/state/storage.action';

export interface DeviceStateModel {
	items: DeviceViewModel[];
	loaded: boolean;
	isHostGateway: boolean; // Determines if the device being used is a gateway
}

const defaultState: DeviceStateModel = {
	items: undefined,
	loaded: false,
	isHostGateway: false,
};

@State<any>({
	name: 'device',
	defaults: defaultState,
})
@Injectable()
export class DeviceState extends AppCleanup {
	constructor(
		private deviceService: DeviceService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private dialogService: DialogService,
		private store: Store
	) {
		super();
	}

	@Action(DeviceActions.GetAll)
	getAll(ctx: StateContext<DeviceStateModel>, action: DeviceActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.deviceService
			.getDevices()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (devices) => {
					let isHostGateway: boolean = false;

					if (devices.length === 1 && devices[0].deviceType.name === DeviceType.GATEWAY) {
						isHostGateway = true;
					}

					ctx.setState({
						items: devices,
						loaded: true,
						isHostGateway: isHostGateway,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.DEVICE.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DeviceActions.SetAll)
	setAll(ctx: StateContext<DeviceStateModel>, action: DeviceActions.SetAll) {
		let isHostGateway: boolean = false;

		if (action.devices.length === 1 && action.devices[0].deviceType.name === DeviceType.GATEWAY) {
			isHostGateway = true;
		}

		ctx.setState({
			items: action.devices,
			loaded: true,
			isHostGateway: isHostGateway,
		});
	}

	@Action(DeviceActions.GetTypes)
	getTypes(ctx: StateContext<DeviceStateModel>, action: DeviceActions.GetTypes) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.deviceService
			.getDeviceTypes()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (types) => {
					this.store.dispatch(new StorageActions.SetDeviceTypes(types));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.DEVICE.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DeviceActions.Create)
	create(ctx: StateContext<DeviceStateModel>, action: DeviceActions.Create) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.deviceService
			.createDevice(action.data.device)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (device) => {
					ctx.setState(
						patch({
							items: append([device]),
						})
					);

					ctx.dispatch(new DeviceActions.CreateSuccess(device));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new DeviceActions.CreateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DeviceActions.Update)
	update(ctx: StateContext<DeviceStateModel>, action: DeviceActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.deviceService
			.updateDevice(action.data.device)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (device) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === device.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((dv) => dv.id === action.data.device.id, device),
							})
						);
					}

					ctx.dispatch(new DeviceActions.UpdateSuccess(device));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new DeviceActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DeviceActions.Delete)
	delete(ctx: StateContext<DeviceStateModel>, action: DeviceActions.Delete) {
		let deleteDevice = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.deviceService
				.deprovisionDevice(action.data.machineName)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.machineName === action.data.machineName);

						if (existingItem) {
							ctx.setState(
								patch({
									items: removeItem((device) => device.machineName === action.data.machineName),
								})
							);
						}

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DEPROVISIONED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DEPROVISIONED', { name: this.translateService.instant('MODULE.DEVICE.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DEPROVISION'))} ${this.translateService.instant('MODULE.DEVICE.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DEPROVISION', { name: this.translateService.instant('MODULE.DEVICE.TITLE') })}`,
				action: this.translateService.instant('ACTION.DEPROVISION_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteDevice();
					}
				});
		} else {
			deleteDevice();
		}
	}

	@Selector()
	static isLoaded(state: DeviceStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static devices(state: DeviceStateModel): DeviceViewModel[] {
		return state.items;
	}

	@Selector()
	static isHostGateway(state: DeviceStateModel): boolean {
		return state.isHostGateway;
	}
}
